import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";

interface OutreachWidgetProps {
  projectId: string;
}

export default function OutreachWidget({ projectId }: OutreachWidgetProps) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOutreach();
  }, [projectId]);

  const fetchOutreach = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from("project_notes")
        .select("content")
        .eq("project_id", projectId)
        .eq("note_type", "outreach")
        .order("display_order", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;
      setContent(data?.content || null);
    } catch (err) {
      console.error("Error fetching outreach:", err);
      setError("Failed to load outreach");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader color="indigo">
          <h3 className="text-base sm:text-lg font-bold text-black">
            Current Outreach
          </h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-gray-500 text-xs sm:text-sm">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !content) {
    return null; // Hide widget if no content
  }

  // Split content by bullet points and filter empty lines
  const items = content
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return (
    <Card variant="bordered">
      <CardHeader color="indigo">
        <h3 className="text-base sm:text-lg font-bold text-black">
          Current Outreach
        </h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="text-indigo-600 font-bold flex-shrink-0">•</span>
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                {item.replace(/^[•\-]\s*/, "")}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
